/**
 * Reference ranges for common lab parameters
 * Used when the API doesn't provide reference ranges with the lab values
 */

export const LAB_REFERENCE_RANGES = {
  // Lipid Panel
  'labs_lipid_chol_total': '< 200',
  'labs_lipid_hdl': '> 40',
  'labs_lipid_ldl': '< 100',
  'labs_lipid_trig': '< 150',
  'labs_lipid_non_hdl': '< 130',
  
  // Complete Blood Count (CBC)
  'labs_cbc_wbc': '4.0-11.0',
  'labs_cbc_rbc': '4.2-5.4',
  'labs_cbc_hgb': '12.0-15.5',
  'labs_cbc_hct': '36.0-46.0',
  'labs_cbc_plt': '150-450',
  'labs_cbc_mcv': '80-100',
  'labs_cbc_mch': '27-32',
  'labs_cbc_mchc': '32-36',
  
  // Comprehensive Metabolic Panel (CMP)
  'labs_cmp_glucose': '70-100',
  'labs_cmp_creatinine': '0.6-1.1',
  'labs_renal_creatinine': '0.6-1.1',
  'labs_renal_urea': '7-20',
  'labs_renal_bun': '7-20',
  'labs_renal_egfr': '> 60',
  
  // Glucose/Diabetes
  'labs_glucose_glucose': '70-100',
  'labs_glucose_hba1c': '< 5.7',
  
  // Liver Function
  'labs_liver_alt': '7-40',
  'labs_hepatology_alt': '7-40',
  'labs_hepatology_ast': '10-40',
  'labs_hepatology_alp': '44-147',
  'labs_hepatology_bili_total': '0.2-1.2',
  'labs_hepatology_bili_direct': '0.0-0.3',
  'labs_hepatology_bili_indirect': '0.2-0.8',
  
  // Electrolytes
  'labs_electrolytes_sodium': '136-145',
  'labs_electrolytes_potassium': '3.5-5.0',
  'labs_electrolytes_chloride': '98-107',
  'labs_electrolytes_bicarb': '22-28',
  'labs_electrolytes_calcium': '8.5-10.5',
  'labs_electrolytes_magnesium': '1.7-2.2',
  'labs_electrolytes_phosphorous': '2.5-4.5',
  
  // Thyroid
  'labs_thyroid_tsh': '0.4-4.0',
  'labs_thyroid_ft4': '0.8-1.8',
  
  // Cardiac
  'labs_cardiac_troponin': '< 0.04',
  'labs_cardiac_bnp': '< 100',
  
  // Inflammation
  'labs_inflammation_hscrp': '< 3.0',
  'labs_inflammation_uric_acid': '3.5-7.2',
  
  // Proteins
  'labs_protein_total': '6.0-8.3',
  'labs_protein_albumin': '3.5-5.0',
  'labs_protein_prealbumin': '20-40',
  
  // Vitamins
  'labs_vitamins_d3': '30-100',
  'labs_vitamins_b12': '200-900',
  'labs_vitamins_b9': '> 3.0',
  'labs_vitamins_iron': '60-170',
  'labs_vitamins_ferritin': '12-150',
  
  // Coagulation
  'labs_coagulation_pt': '11.0-13.0',
  'labs_coagulation_ptt': '25-35',
  'labs_coagulation_inr': '0.8-1.1',
  
  // Urinalysis
  'labs_urine_protein': '< 30',
  'labs_urine_glucose': 'Negative',
  'labs_urine_ketones': 'Negative',
  'labs_urine_microalb': '< 30',
  'labs_urine_uacr': '< 30',
  'labs_urine_creat': '20-320',
  
  // Additional calculated values
  'labs_lipid_athero_idx': '< 0.5',
  'labs_lipid_tc_hdl_ratio': '< 5.0',
  'labs_lipid_tg_hdl_ratio': '< 3.0',
  'labs_lipid_tc_glu_ratio': '< 2.0',
  'labs_cbc_nlr': '< 3.0',
  'labs_renal_bun_creat_ratio': '10-20',
  'labs_electrolytes_na_k_ratio': '27-40',
  'labs_protein_alb_glob_ratio': '1.1-2.5'
};

/**
 * Get reference range for a lab parameter
 * @param {string} parameterName - The lab parameter name
 * @returns {string|null} - The reference range or null if not found
 */
export const getLabReferenceRange = (parameterName) => {
  return LAB_REFERENCE_RANGES[parameterName] || null;
};

/**
 * Get units for common lab parameters
 * @param {string} parameterName - The lab parameter name
 * @returns {string} - The units for the parameter
 */
export const getLabUnits = (parameterName) => {
  const UNITS_MAP = {
    // Lipids
    'labs_lipid_chol_total': 'mg/dL',
    'labs_lipid_hdl': 'mg/dL',
    'labs_lipid_ldl': 'mg/dL',
    'labs_lipid_trig': 'mg/dL',
    'labs_lipid_non_hdl': 'mg/dL',
    
    // CBC
    'labs_cbc_wbc': 'x10^9/L',
    'labs_cbc_rbc': 'x10^12/L',
    'labs_cbc_hgb': 'g/dL',
    'labs_cbc_hct': '%',
    'labs_cbc_plt': 'x10^9/L',
    'labs_cbc_mcv': 'fL',
    'labs_cbc_mch': 'pg',
    'labs_cbc_mchc': 'g/dL',
    
    // Chemistry
    'labs_cmp_glucose': 'mg/dL',
    'labs_cmp_creatinine': 'mg/dL',
    'labs_renal_creatinine': 'mg/dL',
    'labs_glucose_glucose': 'mg/dL',
    'labs_glucose_hba1c': '%',
    'labs_liver_alt': 'U/L',
    'labs_hepatology_alt': 'U/L',
    'labs_hepatology_ast': 'U/L',
    'labs_hepatology_alp': 'U/L',
    
    // Electrolytes
    'labs_electrolytes_sodium': 'mmol/L',
    'labs_electrolytes_potassium': 'mmol/L',
    'labs_electrolytes_chloride': 'mmol/L',
    'labs_electrolytes_bicarb': 'mmol/L',
    'labs_electrolytes_calcium': 'mg/dL',
    'labs_electrolytes_magnesium': 'mg/dL',
    
    // Thyroid
    'labs_thyroid_tsh': 'mIU/L',
    'labs_thyroid_ft4': 'ng/dL',
    
    // Cardiac
    'labs_cardiac_troponin': 'ng/mL',
    'labs_cardiac_bnp': 'pg/mL',
    
    // Proteins
    'labs_protein_total': 'g/dL',
    'labs_protein_albumin': 'g/dL',
    'labs_protein_prealbumin': 'mg/dL',
    
    // Vitamins
    'labs_vitamins_d3': 'ng/mL',
    'labs_vitamins_b12': 'pg/mL',
    'labs_vitamins_iron': 'μg/dL',
    'labs_vitamins_ferritin': 'ng/mL',
    
    // Coagulation
    'labs_coagulation_pt': 'sec',
    'labs_coagulation_ptt': 'sec',
    
    // Urine
    'labs_urine_protein': 'mg/dL',
    'labs_urine_microalb': 'mg/g',
    'labs_urine_uacr': 'mg/g',
    'labs_urine_creat': 'mg/dL'
  };
  
  return UNITS_MAP[parameterName] || '';
};
